import { useEffect, useState } from 'react';
import { API_BASE_URL, webSocketUrl } from '../config/apiBase';

type CheckStatus = 'pending' | 'pass' | 'fail';

interface Check {
  id: string;
  label: string;
  description: string;
  status: CheckStatus;
  detail?: string;
}

const INITIAL_CHECKS: Omit<Check, 'status'>[] = [
  {
    id: 'portal-api',
    label: 'Portal API',
    description: 'HTTPS to portal.steamroomtrading.com (REST endpoints)',
  },
  {
    id: 'portal-ws',
    label: 'Portal WebSocket',
    description: 'WSS to portal.steamroomtrading.com (live alerts, GEX-Ray, premium)',
  },
  {
    id: 'squawk-webrtc',
    label: 'Squawk WebRTC (audio standard mode)',
    description: 'UDP to squawk.steamroomtrading.com',
  },
  {
    id: 'squawk-hls',
    label: 'Squawk HLS (audio backup mode)',
    description: 'HTTPS to squawk.steamroomtrading.com',
  },
  {
    id: 'sentry',
    label: 'Sentry error reporting',
    description: 'HTTPS to the configured Sentry ingest host',
  },
];

async function runAllChecks(
  setChecks: React.Dispatch<React.SetStateAction<Check[]>>
): Promise<void> {
  const update = (id: string, status: CheckStatus, detail?: string) => {
    setChecks((prev) => prev.map((c) => (c.id === id ? { ...c, status, detail } : c)));
  };

  await Promise.allSettled([
    checkPortalApi(update),
    checkPortalWs(update),
    checkSquawkWebRtc(update),
    checkSquawkHls(update),
    checkSentry(update),
  ]);
}

async function checkPortalApi(
  update: (id: string, status: CheckStatus, detail?: string) => void
): Promise<void> {
  // Same base every other caller uses — '' (same-origin) in production builds.
  // This file used to carry its own divergent fallback.
  const base = API_BASE_URL;
  try {
    // Use /api/users/validate-token/ as a known-good endpoint that responds
    // even without auth (returns 401, which means "reached the server").
    // /api/health/ doesn't exist on this backend.
    const res = await fetch(`${base}/api/users/validate-token/`, {
      credentials: 'omit',
      signal: AbortSignal.timeout(8000),
    });
    // Any HTTP response < 500 means the server is reachable.
    update('portal-api', res.status < 500 ? 'pass' : 'fail', `HTTP ${res.status}`);
  } catch (err) {
    update('portal-api', 'fail', err instanceof Error ? err.message : 'unknown error');
  }
}

async function checkPortalWs(
  update: (id: string, status: CheckStatus, detail?: string) => void
): Promise<void> {
  // Absolute URL required: new WebSocket('/ws/') throws a SyntaxError.
  const wsUrl = webSocketUrl('/ws/');
  return new Promise((resolve) => {
    let ws: WebSocket | null = null;
    const timeout = window.setTimeout(() => {
      ws?.close();
      update('portal-ws', 'fail', 'timeout after 5s');
      resolve();
    }, 5000);
    try {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        window.clearTimeout(timeout);
        update('portal-ws', 'pass', 'connected');
        ws?.close();
        resolve();
      };
      ws.onerror = () => {
        window.clearTimeout(timeout);
        update('portal-ws', 'fail', 'connection error');
        resolve();
      };
    } catch (err) {
      window.clearTimeout(timeout);
      update('portal-ws', 'fail', err instanceof Error ? err.message : 'unknown error');
      resolve();
    }
  });
}

async function checkSquawkWebRtc(
  update: (id: string, status: CheckStatus, detail?: string) => void
): Promise<void> {
  // We can't actually open the SRS WebRTC stream without auth, so use ICE
  // gathering against Google's public STUN server to test whether UDP egress
  // works at all. If the user's firewall blocks UDP, no UDP candidate gets
  // gathered. False positives possible if STUN itself is blocked but UDP
  // works for the real SRS endpoint — acceptable for v1.
  try {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });
    pc.createDataChannel('test');
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const gotUdpCandidate = await new Promise<boolean>((resolve) => {
      const timer = window.setTimeout(() => resolve(false), 3000);
      pc.onicecandidate = (e) => {
        if (e.candidate?.candidate.includes(' udp ')) {
          window.clearTimeout(timer);
          resolve(true);
        }
      };
    });

    pc.close();
    update(
      'squawk-webrtc',
      gotUdpCandidate ? 'pass' : 'fail',
      gotUdpCandidate ? 'UDP egress OK' : 'no UDP candidates (firewall likely blocks UDP)'
    );
  } catch (err) {
    update('squawk-webrtc', 'fail', err instanceof Error ? err.message : 'unknown error');
  }
}

async function checkSquawkHls(
  update: (id: string, status: CheckStatus, detail?: string) => void
): Promise<void> {
  // Check HTTPS reachability to the SRS host. We don't need a working HLS
  // endpoint yet — just confirming the host responds over HTTPS.
  try {
    const res = await fetch('https://squawk.steamroomtrading.com/api/v1/summaries', {
      credentials: 'omit',
      signal: AbortSignal.timeout(8000),
    });
    update(
      'squawk-hls',
      res.status < 500 ? 'pass' : 'fail',
      `HTTP ${res.status}`
    );
  } catch (err) {
    update('squawk-hls', 'fail', err instanceof Error ? err.message : 'unknown error');
  }
}

async function checkSentry(
  update: (id: string, status: CheckStatus, detail?: string) => void
): Promise<void> {
  // Probe the host from our OWN DSN, not a hardcoded one. Sentry ingest hosts are
  // per-org subdomains (o<orgid>.ingest.<region>.sentry.io) — bare `ingest.sentry.io`
  // has no DNS record, so the previous hardcoded probe ALWAYS reported "Failed to
  // fetch" regardless of whether Sentry actually worked. That false negative sent a
  // 2026-09-07 debugging session hunting a nonexistent ad-blocker problem.
  //
  // no-cors means we can't read the response, but a successful fetch (even opaque)
  // proves the network path. A DNS block, firewall, or ad blocker rejects the promise.
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    update('sentry', 'fail', 'no VITE_SENTRY_DSN in this build — errors are NOT reported');
    return;
  }

  let ingestOrigin: string;
  try {
    ingestOrigin = new URL(dsn).origin;
  } catch {
    update('sentry', 'fail', 'VITE_SENTRY_DSN is malformed');
    return;
  }

  try {
    await fetch(ingestOrigin, {
      mode: 'no-cors',
      signal: AbortSignal.timeout(8000),
    });
    update('sentry', 'pass', `reachable (${new URL(ingestOrigin).hostname})`);
  } catch (err) {
    update('sentry', 'fail', err instanceof Error ? err.message : 'unknown error');
  }
}

function CheckRow({ check }: { check: Check }): React.ReactElement {
  const statusColor = {
    pending: 'bg-gray-500',
    pass: 'bg-bull-blue',
    fail: 'bg-bear-red',
  }[check.status];

  return (
    <div className="flex items-center gap-3 p-3 rounded border border-dark-border bg-dark-surface">
      <span className={`w-2 h-2 rounded-full ${check.status === 'pending' ? statusColor + ' animate-pulse' : statusColor}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{check.label}</p>
        <p className="text-xs text-gray-500">{check.description}</p>
        {check.detail && <p className="text-xs text-gray-400 mt-1 truncate">{check.detail}</p>}
      </div>
      <span className="text-xs text-gray-400 w-12 text-right">
        {check.status === 'pending' ? '...' : check.status.toUpperCase()}
      </span>
    </div>
  );
}

export function DiagnosticsPage(): React.ReactElement {
  const [checks, setChecks] = useState<Check[]>(
    INITIAL_CHECKS.map((c) => ({ ...c, status: 'pending' }))
  );

  useEffect(() => {
    void runAllChecks(setChecks);
  }, []);

  const allComplete = checks.every((c) => c.status !== 'pending');
  const anyFail = checks.some((c) => c.status === 'fail');

  return (
    <div className="min-h-screen bg-dark-bg text-white p-6 font-mono">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">Connection Diagnostics</h1>
        <p className="text-sm text-gray-400 mb-6">
          Tests every service the trading dashboard depends on. Share a screenshot
          of this page with support if anything fails.
        </p>

        <div className="space-y-2">
          {checks.map((check) => (
            <CheckRow key={check.id} check={check} />
          ))}
        </div>

        {allComplete && (
          <div className="mt-6 p-4 rounded border border-dark-border bg-dark-surface">
            {anyFail ? (
              <>
                <p className="text-sm text-bear-red font-medium mb-2">
                  Some checks failed. Likely causes:
                </p>
                <ul className="text-xs text-gray-400 space-y-1 list-disc list-inside">
                  <li>Corporate firewall blocking outbound traffic to one or more hosts</li>
                  <li>Browser extension (ad blocker, security plugin) interfering</li>
                  <li>VPN routing some traffic incorrectly</li>
                </ul>
                <p className="text-xs text-gray-400 mt-3">
                  Forward this page (screenshot) to <span className="text-blue-400">contact@fzntrading.com</span>
                </p>
              </>
            ) : (
              <p className="text-sm text-bull-blue">All checks passed. Your connection is healthy.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
